import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HandHeart, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

export const FosterNetwork = () => {
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/foster/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/foster/stories").then(({ data }) => setStories(data.slice(0, 3))).catch(() => {});
  }, []);

  if (!stats) return null;

  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-black" data-testid="home-foster-network">
      <div className="absolute -left-20 top-1/2 h-96 w-96 rounded-full soul-pulse pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,20,147,0.35) 0%, rgba(255,20,147,0) 70%)", filter: "blur(12px)" }} />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline text-primary mb-3 inline-flex items-center gap-2"><HandHeart weight="fill" size={15} /> The Foster Network · Practice What We Preach</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-5xl">
              Not just a shop. A <span style={{ color: "#ccff00" }}>rescue machine.</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-lg">
              Real fosters. Real pits in real homes. Daily check-ins that keep every animal accounted for — every placement building the $3M ranch.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              {[
                { n: stats.active_fosters, l: "Active fosters" },
                { n: stats.active_placements, l: "Pits in care" },
                { n: stats.checkins, l: "Check-ins" },
              ].map((s) => (
                <div key={s.l} className="rounded-xl border border-white/10 px-5 py-3 bg-white/5">
                  <p className="font-display font-black text-2xl neon-text">{s.n}</p>
                  <p className="overline text-muted-foreground text-[10px]">{s.l}</p>
                </div>
              ))}
            </div>
            <Link to="/foster" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-[10px] font-black uppercase text-black hover:opacity-90 transition-opacity" style={{ background: "#ccff00" }} data-testid="home-foster-cta">
              <HandHeart weight="fill" size={17} /> Become a foster <ArrowRight size={17} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {stories.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="relative rounded-xl overflow-hidden border border-white/10" data-testid={`home-foster-story-${s.id}`}>
                <img src={s.image} alt={s.dog} className="aspect-[3/4] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <span className="absolute top-2 left-2 overline text-[9px] px-2 py-0.5 rounded-full text-black font-semibold inline-flex items-center gap-1" style={{ background: "#ccff00" }}>
                  <CheckCircle weight="fill" size={10} /> {s.outcome}
                </span>
                <div className="absolute bottom-2 left-3">
                  <p className="font-display font-black uppercase text-lg leading-none">{s.dog}</p>
                  <p className="overline text-white/70 text-[9px]">{s.foster}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FosterNetwork;
